import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { generateReferralLink } from 'App/components/PatientProfile/PatientProfile.utils.js';
import SelectColumnFilter from 'App/components/Table/SelectColumnFilter';
import Table from 'App/components/Table/Table';
import { sortLocalizedDate } from 'App/components/Table/sortTypes';
import { FaInfoCircle } from 'react-icons/fa';
import IconWrapper from 'App/common/styles/IconWrapper';
import { DateCell, NameCell, NotesCell, NoteStatusDateCell } from './components/ActivitiesColumns';
import * as R from 'ramda';
import hasLength from 'App/utils/hasLength';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import { PERMISSION_TYPES } from 'App/utils/isPermitted';
import IsMobileCell from './components/IsMobileCell';
import { ActivityTypes } from './PatientActivities';
import useGetPatientActivities from 'App/components/PatientProfile/hooks/useGetPatientActivities';

const getStatusForActivityType = (activityType) => {
    switch (activityType) {
        case ActivityTypes.PastActivities:
            return 'completed';
        case ActivityTypes.DeletedActivities:
            return 'deleted';
        case ActivityTypes.PendingActivities:
        default:
            return 'pending';
    }
};

const propTypes = {
    id: PropTypes.string.isRequired,
    activityType: PropTypes.number.isRequired,
    divisionId: PropTypes.string,
    openActivityModal: PropTypes.func.isRequired,
    openActivityNoteModal: PropTypes.func.isRequired,
};

const defaultProps = {
    divisionId: '',
};

const PatientActivitiesContent = ({ id, activityType, divisionId, openActivityModal, openActivityNoteModal }) => {
    const { activities, isActivityLoading } = useGetPatientActivities(id, getStatusForActivityType(activityType));

    const isGenerateLinkEnabled = useIsPermitted(PERMISSION_TYPES.GENERATE_MOBILE_LINK);
    const isSmsSendLinkEnabled = useIsPermitted(PERMISSION_TYPES.SMS_SEND_LINK);

    const columns = useMemo(() => {
        return [
            {
                Header: 'Activity Name',
                accessor: 'name',
                Cell: NameCell({ divisionId, generateReferralLink }),
                style: { width: '25%' },
            },
            ...(R.allPass([
                R.anyPass([R.always(isGenerateLinkEnabled), R.always(isSmsSendLinkEnabled)]),
                R.equals(ActivityTypes.PendingActivities),
            ])(activityType)
                ? [
                      {
                          Header: 'Mobile',
                          Filter: SelectColumnFilter,
                          accessor: R.ifElse(
                              R.pathOr(false, ['settings', 'mobileEnabled']),
                              R.always('Yes'),
                              R.always('No')
                          ),
                          Cell: IsMobileCell,
                          style: { width: '10%' },
                      },
                  ]
                : []),
            {
                Header: 'Type',
                accessor: 'typeLabel',
                Filter: SelectColumnFilter,
                style: { width: ActivityTypes.DeletedActivities === activityType ? '12%' : '18%' },
            },
            {
                Header: 'Status',
                accessor: 'status',
                style: { width: ActivityTypes.DeletedActivities === activityType ? '15.75%' : '19%' },
            },
            ...(R.anyPass([R.equals(ActivityTypes.PendingActivities), R.equals(ActivityTypes.PastActivities)])(
                activityType
            )
                ? [
                      {
                          Header: 'Medication Association',
                          accessor: R.compose(
                              R.ifElse(hasLength, R.compose(R.join(', '), R.map(R.prop('name'))), R.always('--')),
                              R.propOr([], 'medAssociation')
                          ),
                          disableFilters: true,
                          style: { width: '25%' },
                      },
                  ]
                : []),
            ...(ActivityTypes.PendingActivities === activityType || ActivityTypes.DeletedActivities === activityType
                ? [
                      {
                          Header: 'Due Date',
                          accessor: 'dateDue',
                          Cell: NoteStatusDateCell({ activityType, openModal: openActivityModal }),
                          sortType: sortLocalizedDate,
                          disableFilters: true,
                          style: { width: ActivityTypes.DeletedActivities === activityType ? '15.75%' : '19%' },
                      },
                  ]
                : []),
            ...(ActivityTypes.PastActivities === activityType
                ? [
                      {
                          Header: 'Completed Date',
                          accessor: 'dateCompleted',
                          Cell: DateCell,
                          sortType: sortLocalizedDate,
                          disableFilters: true,
                          style: { width: ActivityTypes.DeletedActivities === activityType ? '15.75%' : '19%' },
                      },
                  ]
                : []),
            {
                Header: 'Notes Status',
                accessor: 'noteStatus',
                Cell: NoteStatusDateCell({ activityType, openModal: openActivityNoteModal }),
                disableFilters: true,
                style: { width: ActivityTypes.DeletedActivities === activityType ? '15.75%' : '19%' },
            },
            ...(ActivityTypes.DeletedActivities === activityType
                ? [
                      {
                          Header: 'Actions',
                          Cell: NotesCell({ openModal: openActivityNoteModal }),
                          disableFilters: true,
                          style: { width: ActivityTypes.DeletedActivities === activityType ? '15.75%' : '19%' },
                      },
                  ]
                : []),
        ];
    }, [
        divisionId,
        isGenerateLinkEnabled,
        isSmsSendLinkEnabled,
        activityType,
        openActivityModal,
        openActivityNoteModal,
    ]);

    const getInitialState = useCallback(() => {
        switch (activityType) {
            case ActivityTypes.PendingActivities:
                return {
                    sortBy: [
                        {
                            id: 'dateDue',
                            desc: false,
                        },
                    ],
                    pageSize: 5,
                };
            case ActivityTypes.PastActivities:
                return {
                    sortBy: [
                        {
                            id: 'dateCompleted',
                            desc: true,
                        },
                    ],
                    pageSize: 5,
                };
            case ActivityTypes.DeletedActivities:
                return {
                    sortBy: [
                        {
                            id: 'dateDue',
                            desc: true,
                        },
                    ],
                    pageSize: 5,
                };
            default:
                return {};
        }
    }, [activityType]);

    return hasLength(activities) ? (
        <Table
            className={`activities-${id}-${activityType}`}
            data-testid={`activities-${id}-${activityType}`}
            columns={columns}
            data={activities}
            placeholderText="No available activities"
            initialState={getInitialState()}
            pageSizeOptions={[5, 10, 25]}
            isLoading={isActivityLoading}
        />
    ) : (
        <div>
            <IconWrapper>
                <FaInfoCircle />
            </IconWrapper>
            <span>There are currently no activities to display.</span>
        </div>
    );
};

PatientActivitiesContent.propTypes = propTypes;
PatientActivitiesContent.defaultProps = defaultProps;

export default PatientActivitiesContent;
